import React, { useState } from 'react';
import { Alert, Divider, Grid, MenuItem, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Form, Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { useSWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
import { ApiError, OrgRole, OrgUserOut, UserCredentialsOut, UserService } from '../api';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import { isMultiLocation } from '../common/filter/locationGrouping';
import { LocationChangeDialog } from './LocationChangeDialog';
import { UserLocationSelect } from './UserLocationSelect';
import { ASSIGNABLE_ROLES, apiErrorDetail, isReservedEmail, userFullName } from './userManagementUtils';

const EMAIL_EXISTS_DETAIL = 'email_exists';

const ERROR_MESSAGE_KEY_BY_DETAIL: Record<string, string> = {
  last_admin: 'users.errors.last_admin',
  cannot_block_self: 'users.drawer.block_self',
  cannot_change_own_role: 'users.drawer.role_self',
  user_blocked: 'users.errors.user_blocked',
  user_not_editable: 'users.errors.user_not_editable',
  location_not_found: 'users.errors.location_not_found',
  [EMAIL_EXISTS_DETAIL]: 'users.errors.email_exists',
};

type UserDetailsTabProps = {
  user: OrgUserOut;
  onChanged: (updatedUser: OrgUserOut) => void;
  onCredentials: (credentials: UserCredentialsOut) => void;
  isLastEnabledAdmin: boolean;
  qualifiedOrderPageIds: string[];
};

type EditUserFormValues = {
  email: string;
  first_name: string;
  last_name: string;
  role: OrgRole;
  location_id: string | null;
  is_floating: boolean;
};

export const UserDetailsTab = ({
  user,
  onChanged,
  onCredentials,
  isLastEnabledAdmin,
  qualifiedOrderPageIds,
}: UserDetailsTabProps) => {
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const { account } = useGetAccountAuthenticated({});
  const safeExecute = useSafeExecute();
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);

  const fullName = userFullName(user);
  const isSelf = user.is_current_user;
  const shouldShowLocations = !!account && isMultiLocation(account);
  const isLastAdminProtected = isLastEnabledAdmin && user.enabled && user.role === OrgRole.ADMIN;
  const isBlockDisabled = isSelf || isLastAdminProtected;

  const generateErrorMessage = (error: ApiError): string => {
    const messageKey = ERROR_MESSAGE_KEY_BY_DETAIL[apiErrorDetail(error) ?? ''];
    return messageKey ? t(messageKey) : error.body?.message || t('api.error.default');
  };

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .required(t('users.errors.required'))
      .email(t('users.errors.invalid_email'))
      .test('reserved-email', t('users.errors.reserved_email'), (email) => !isReservedEmail(email)),
    first_name: yup.string().trim().required(t('users.errors.required')),
    last_name: yup.string().trim().required(t('users.errors.required')),
    role: yup.string().required(t('users.errors.required')),
  });

  const initialValues: EditUserFormValues = {
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    role: user.role,
    location_id: user.location_id ?? null,
    is_floating: !!user.is_floating,
  };

  const execute = async <T,>(apiCall: () => Promise<T>, successMessage?: string): Promise<T | undefined> => {
    setIsBusy(true);
    try {
      return await safeExecute({
        apiCall,
        successMessage: successMessage ?? '',
        suppressSuccessMessage: !successMessage,
        onErrorMessageGenerator: generateErrorMessage,
      });
    } catch {
      return undefined;
    } finally {
      setIsBusy(false);
    }
  };

  const updateUser = async (
    values: EditUserFormValues,
    setFieldError: FormikHelpers<EditUserFormValues>['setFieldError'],
  ) => {
    setEmailExists(false);
    try {
      const updatedUser = await safeExecute({
        apiCall: () =>
          UserService.updateOrgUser({
            user_id: user.user_id,
            email: values.email.trim(),
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            role: values.role,
            location_id: values.location_id ?? undefined,
            is_floating: values.is_floating,
          }),
        successMessage: t('users.saved'),
        onErrorMessageGenerator: generateErrorMessage,
      });
      onChanged(updatedUser);
      await mutate({ opId: 'getUserPreferences', userId: user.user_id });
    } catch (error) {
      if (apiErrorDetail(error) === EMAIL_EXISTS_DETAIL) {
        setEmailExists(true);
        setFieldError('email', t('users.errors.email_exists_field'));
      }
    }
  };

  const onSubmit = async (values: EditUserFormValues, { setFieldError }: FormikHelpers<EditUserFormValues>) => {
    if (
      shouldShowLocations &&
      (values.location_id !== (user.location_id ?? null) || values.is_floating !== !!user.is_floating)
    ) {
      setIsLocationDialogOpen(true);
      return;
    }
    await updateUser(values, setFieldError);
  };

  const handleResetDialogClose = async (isConfirmed: boolean) => {
    if (isConfirmed) {
      const credentials = await execute(() => UserService.resetOrgUserPassword({ user_id: user.user_id }));
      if (credentials) {
        onCredentials(credentials);
      }
    }
    setIsResetDialogOpen(false);
  };

  const handleBlockDialogClose = async (isConfirmed: boolean) => {
    if (isConfirmed) {
      const blockedUser = await execute(
        () => UserService.blockOrgUser({ user_id: user.user_id }),
        t('users.blocked_success', { name: fullName }),
      );
      if (blockedUser) {
        onChanged(blockedUser);
      }
    }
    setIsBlockDialogOpen(false);
  };

  const handleUnblock = async () => {
    const unblockedUser = await execute(
      () => UserService.unblockOrgUser({ user_id: user.user_id }),
      t('users.unblocked_success', { name: fullName }),
    );
    if (unblockedUser) {
      onChanged(unblockedUser);
    }
  };

  const blockBody = isSelf
    ? t('users.drawer.block_self')
    : isLastAdminProtected
    ? t('users.errors.last_admin')
    : t('users.drawer.block_body', { firstName: user.first_name });

  return (
    <>
      <Grid container flexDirection="column" flexWrap="nowrap" gap={2.5} sx={{ maxWidth: 640 }}>
        {!user.enabled && (
          <Alert severity="info">{t('users.drawer.blocked_info', { firstName: user.first_name })}</Alert>
        )}
        <Typography variant="h6">{t('users.drawer.details')}</Typography>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}>
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            dirty,
            setValues,
            setFieldError,
            setSubmitting,
          }) => (
            <>
              <Form noValidate>
                <Grid container flexDirection="column" gap={2}>
                  {emailExists && <Alert severity="error">{t('users.errors.email_exists')}</Alert>}
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="first_name"
                        label={t('order_page.firstname')}
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.first_name && !!errors.first_name}
                        helperText={touched.first_name ? errors.first_name : undefined}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="last_name"
                        label={t('order_page.lastname')}
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.last_name && !!errors.last_name}
                        helperText={touched.last_name ? errors.last_name : undefined}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    fullWidth
                    size="small"
                    name="email"
                    label={t('users.fields.email')}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && !!errors.email}
                    helperText={touched.email ? errors.email : undefined}
                    inputProps={{ dir: 'ltr' }}
                  />
                  {values.email.trim() !== user.email && (
                    <Alert severity="warning">
                      {t('users.drawer.email_change_warning', { firstName: user.first_name })}
                    </Alert>
                  )}
                  <TextField
                    select
                    fullWidth
                    size="small"
                    name="role"
                    label={t('users.fields.role')}
                    value={values.role}
                    onChange={handleChange}
                    disabled={isSelf || isLastAdminProtected}
                    helperText={
                      isSelf
                        ? t('users.drawer.role_self')
                        : isLastAdminProtected
                        ? t('users.errors.last_admin')
                        : t('users.role_help')
                    }>
                    {ASSIGNABLE_ROLES.map((role) => (
                      <MenuItem key={role} value={role}>
                        {t(`users.roles.${role}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  {shouldShowLocations && (
                    <UserLocationSelect
                      value={{ locationId: values.location_id, isFloating: values.is_floating }}
                      onChange={({ locationId, isFloating }) =>
                        setValues({ ...values, location_id: locationId, is_floating: isFloating })
                      }
                      helperText={t('users.location_help_edit')}
                    />
                  )}
                  <Grid container>
                    <LoadingButton
                      loading={isSubmitting}
                      disabled={!dirty}
                      type="submit"
                      variant="contained"
                      sx={{ textTransform: 'none' }}>
                      {t('save_changes')}
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Form>
              <LocationChangeDialog
                open={isLocationDialogOpen}
                userName={fullName}
                fromLocationId={user.location_id ?? null}
                fromIsFloating={!!user.is_floating}
                toLocationId={values.location_id}
                toIsFloating={values.is_floating}
                qualifiedOrderPageIds={qualifiedOrderPageIds}
                onCancel={() => setIsLocationDialogOpen(false)}
                onConfirm={async () => {
                  setIsLocationDialogOpen(false);
                  setSubmitting(true);
                  await updateUser(values, setFieldError);
                  setSubmitting(false);
                }}
              />
            </>
          )}
        </Formik>
        {user.enabled && (
          <>
            <Divider />
            <Grid container flexDirection="column" alignItems="flex-start" gap={1}>
              <Typography variant="h6">{t('users.drawer.password_title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t(isSelf ? 'users.drawer.password_body_self' : 'users.drawer.password_body', {
                  firstName: user.first_name,
                })}
              </Typography>
              <Button
                variant="outlined"
                disabled={isBusy}
                sx={{ textTransform: 'none' }}
                onClick={() => setIsResetDialogOpen(true)}>
                {t('users.drawer.reset_password')}
              </Button>
            </Grid>
            <Divider />
            <Grid container flexDirection="column" alignItems="flex-start" gap={1}>
              <Typography variant="h6">{t('users.drawer.block_title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {blockBody}
              </Typography>
              <Button
                variant="outlined"
                color="error"
                disabled={isBusy || isBlockDisabled}
                sx={{ textTransform: 'none' }}
                onClick={() => setIsBlockDialogOpen(true)}>
                {t('users.drawer.block_button')}
              </Button>
            </Grid>
          </>
        )}
        {!user.enabled && (
          <>
            <Divider />
            <Grid container flexDirection="column" alignItems="flex-start" gap={1}>
              <Typography variant="h6">{t('users.drawer.unblock_title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('users.drawer.unblock_body', { firstName: user.first_name })}
              </Typography>
              <Button variant="outlined" disabled={isBusy} sx={{ textTransform: 'none' }} onClick={handleUnblock}>
                {t('users.drawer.unblock_button')}
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      <ConfirmationDialog
        isOpen={isBlockDialogOpen}
        onClose={handleBlockDialogClose}
        title={t('users.block_dialog.title', { name: fullName })}
        content={
          <Grid container flexDirection="column" gap={1.5}>
            <Typography>{t('users.block_dialog.body', { firstName: user.first_name })}</Typography>
            <Typography component="ul" variant="body2" sx={{ m: 0, paddingInlineStart: 2.5 }}>
              <li>{t('users.block_dialog.availability')}</li>
              <li>{t('users.block_dialog.shifts')}</li>
              <li>{t('users.block_dialog.history')}</li>
            </Typography>
          </Grid>
        }
        ctaText={t('users.block_dialog.confirm')}
        confirmButtonColor="error"
      />
      <ConfirmationDialog
        isOpen={isResetDialogOpen}
        onClose={handleResetDialogClose}
        title={t('users.reset_dialog.title', { name: fullName })}
        content={<Typography>{t('users.reset_dialog.body')}</Typography>}
        ctaText={t('users.reset_dialog.confirm')}
        confirmButtonColor="primary"
      />
    </>
  );
};
